import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import './Profile.css';

const base64urlToBuffer = (base64url) => {
  if (!base64url) return base64url;
  if (base64url instanceof ArrayBuffer) return base64url;
  if (ArrayBuffer.isView(base64url)) {
    return base64url.buffer.slice(base64url.byteOffset, base64url.byteOffset + base64url.byteLength);
  }
  if (typeof base64url === 'object') {
    if (Array.isArray(base64url)) return Uint8Array.from(base64url).buffer;
    if (base64url.data !== undefined) return base64urlToBuffer(base64url.data);
    return base64url;
  }
  if (typeof base64url !== 'string') return base64url;
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
};

export default function AccountSettings({ onClose }) {
  const {
    apiFetch,
    token,
    registerPasskeyRequest,
    registerPasskeyResponse,
    listPasskeys,
    removePasskey,
    getProfile,
    disableMfa
  } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState('password');

  // password
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const submitChangePassword = async (e) => {
    e && e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch('/api/auth/change-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to change password');
      setSuccess(true);
      setNewPassword(''); setConfirm('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // MFA
  const [mfaQr, setMfaQr] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaMsg, setMfaMsg] = useState('');
  const [mfaStarted, setMfaStarted] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  const startMfaSetup = async () => {
    if (mfaStarted) return;
    setMfaStarted(true);
    setMfaMsg('');
    try {
      const res = await fetch('/api/users/mfa/setup', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start MFA setup');
      setMfaQr(data.qr || null);
    } catch (err) {
      setMfaMsg(err.message);
    }
  };

  useEffect(() => {
    const loadMfaState = async () => {
      if (tab !== 'mfa') return;
      const profile = await getProfile();
      const enabled = !!(profile && profile.mfaEnabled);
      setMfaEnabled(enabled);
      if (!enabled) {
        startMfaSetup();
      }
    };
    loadMfaState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const enableMfa = async () => {
    setMfaMsg('');
    try {
      const res = await fetch('/api/users/mfa/enable', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: mfaCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to enable MFA');
      setMfaMsg(t('app.accountSettings.mfa.enabled') || 'MFA enabled');
      setMfaQr(null);
      setMfaCode('');
      setMfaEnabled(true);
    } catch (err) {
      setMfaMsg(err.message);
    }
  };

  const disableMfaForAccount = async () => {
    setMfaMsg('');
    const res = await disableMfa(mfaCode);
    if (!res.success) {
      setMfaMsg(res.error || 'Failed to disable MFA');
      return;
    }
    setMfaMsg(t('app.accountSettings.mfa.disabled') || 'MFA disabled');
    setMfaEnabled(false);
    setMfaCode('');
    setMfaStarted(false);
    setTimeout(() => startMfaSetup(), 0);
  };

  // Passkey
  const [pkMsg, setPkMsg] = useState('');
  const [passkeys, setPasskeys] = useState([]);
  const [passkeyName, setPasskeyName] = useState('');
  const [showPkNamePrompt, setShowPkNamePrompt] = useState(false);

  const refreshPasskeys = async () => {
    const res = await listPasskeys();
    if (!res.success) {
      setPkMsg(res.error || 'Failed to load passkeys');
      setPasskeys([]);
      return;
    }
    setPasskeys(res.credentials || []);
  };

  const registerPasskey = async () => {
    if (!showPkNamePrompt) {
      setShowPkNamePrompt(true);
      return;
    }
    setPkMsg('');
    try {
      if (!passkeyName.trim()) {
        setPkMsg(t('app.accountSettings.passkeys.nameRequired') || 'Passkey name is required');
        return;
      }
      const start = await registerPasskeyRequest();
      if (!start || !start.success) {
        if (start && start.error && start.error.toLowerCase().includes('webauthn')) {
          setPkMsg(t('app.webauthn.notEnabled') || 'WebAuthn is not enabled on the server. Run `npm install` in the server and set FRONTEND_URL/WEBAUTHN_RP_ID, then restart.');
          return;
        }
        throw new Error(start && start.error ? start.error : 'Failed to get registration options');
      }
      const options = start.options;
      options.challenge = base64urlToBuffer(options.challenge);
      if (options.user && options.user.id) options.user.id = base64urlToBuffer(options.user.id);
      if (Array.isArray(options.excludeCredentials)) {
        options.excludeCredentials = options.excludeCredentials.map(c => ({ ...c, id: base64urlToBuffer(c.id) }));
      }
      const cred = await navigator.credentials.create({ publicKey: options });
      if (!cred) throw new Error('Credential creation failed');
      const attestation = {
        id: cred.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(cred.rawId))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''),
        response: {
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(cred.response.clientDataJSON))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''),
          attestationObject: btoa(String.fromCharCode(...new Uint8Array(cred.response.attestationObject))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
        },
        type: cred.type,
        name: passkeyName.trim()
      };
      const finish = await registerPasskeyResponse(attestation);
      if (!finish || !finish.success) throw new Error(finish.error || 'Failed to register passkey');
      setPkMsg(t('app.accountSettings.passkeys.registered') || 'Passkey registered');
      setShowPkNamePrompt(false);
      setPasskeyName('');
      refreshPasskeys();
    } catch (err) {
      setPkMsg(err.message || String(err));
      setShowPkNamePrompt(false);
    }
  };

  useEffect(() => {
    if (tab === 'passkeys') {
      refreshPasskeys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const removePasskeyForAccount = async (credentialId) => {
    setPkMsg('');
    const res = await removePasskey(credentialId);
    if (!res.success) {
      setPkMsg(res.error || 'Failed to remove passkey');
      return;
    }
    setPkMsg(t('app.accountSettings.passkeys.removed') || 'Passkey removed');
    refreshPasskeys();
  };

  return (
    <div className="cp-overlay" onClick={() => onClose && onClose()}>
      <div className="cp-modal" onClick={e => e.stopPropagation()}>
        <div className="cp-modal-header">
          <h2>{t('app.accountSettings.title') || 'Account settings'}</h2>
          <button className="cp-close" onClick={() => onClose && onClose()}>X</button>
        </div>

        <div className="account-modal-body" style={{ display: 'flex', gap: 12, padding: 12 }}>
            <div className="tabs-column">
            <button className={`cp-tab ${tab === 'password' ? 'active' : ''}`} onClick={() => setTab('password')}>{t('app.accountSettings.tabs.password') || 'Password'}</button>
            <button className={`cp-tab ${tab === 'mfa' ? 'active' : ''}`} onClick={() => setTab('mfa')}>{t('app.accountSettings.tabs.mfa') || 'MFA (TOTP)'}</button>
            <button className={`cp-tab ${tab === 'passkeys' ? 'active' : ''}`} onClick={() => setTab('passkeys')}>{t('app.accountSettings.tabs.passkeys') || 'Passkeys'}</button>
          </div>

          <div className="content-column">
            {tab === 'password' && (
              <form onSubmit={submitChangePassword} className="cp-form">
                <div className="cp-field">
                  <label>{t('app.changePassword.newPassword') || 'New password'}</label>
                  <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder={t('app.changePassword.newPasswordPlaceholder') || ''} required />
                </div>
                <div className="cp-field">
                  <label>{t('app.changePassword.confirmPassword') || 'Confirm'}</label>
                  <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={t('app.changePassword.confirmPasswordPlaceholder') || ''} required />
                </div>
                {error && <div className="cp-error">{error}</div>}
                {success && <div className="cp-success">{t('app.changePassword.success') || 'Password changed.'}</div>}
                <div className="cp-actions">
                  <button type="button" className="cp-btn-secondary" onClick={() => onClose && onClose()}>{t('app.changePassword.close') || 'Close'}</button>
                  <button type="submit" className="cp-btn-primary" disabled={loading}>{loading ? (t('app.changePassword.saving') || 'Saving...') : (t('app.changePassword.save') || 'Save')}</button>
                </div>
              </form>
            )}

            {tab === 'mfa' && (
              <div>
                {mfaEnabled ? (
                  <div className="cp-form">
                    <div className="cp-field">
                      <label>{t('app.accountSettings.mfa.status') || 'MFA status'}</label>
                      <div>{t('app.accountSettings.mfa.enabled') || 'Enabled'}</div>
                    </div>
                    <div className="cp-field">
                      <label>{t('app.accountSettings.mfa.enterCodeToDisable') || 'Enter current MFA code to disable'}</label>
                      <input placeholder="123456" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} />
                    </div>
                    <div className="cp-actions">
                      <button type="button" className="cp-btn-secondary" onClick={disableMfaForAccount}>{t('app.accountSettings.mfa.disable') || 'Disable MFA'}</button>
                    </div>
                    <div className="muted">{mfaMsg}</div>
                  </div>
                ) : mfaQr ? (
                  <div>
                    <div dangerouslySetInnerHTML={{ __html: `<img src="${mfaQr}" alt="MFA QR" />` }} />
                      <p>{t('app.accountSettings.mfa.setupHelp') || 'Scan the QR with an authenticator app and enter the code to enable.'}</p>
                      <div className="cp-field">
                        <input placeholder="123456" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} />
                      </div>
                      <div className="cp-actions">
                        <button type="button" className="cp-btn-primary" onClick={enableMfa}>{t('app.accountSettings.mfa.enable') || 'Enable MFA'}</button>
                      </div>
                    <div className="muted">{mfaMsg}</div>
                  </div>
                ) : (
                  <div>
                    <div className="cp-actions">
                      <button type="button" className="cp-btn-primary" onClick={startMfaSetup}>{t('app.accountSettings.mfa.setup') || 'Setup MFA (TOTP)'}</button>
                    </div>
                    <div className="muted">{mfaMsg}</div>
                  </div>
                )}
              </div>
            )}

            {tab === 'passkeys' && (
              <div className="cp-form">
                {showPkNamePrompt ? (
                  <div className="cp-field">
                    <label>{t('app.accountSettings.passkeys.nameLabel') || 'Passkey name'}</label>
                    <input
                      placeholder={t('app.accountSettings.passkeys.namePlaceholder') || 'e.g., My MacBook Pro'}
                      value={passkeyName}
                      onChange={(e) => setPasskeyName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && registerPasskey()}
                    />
                    <div className="muted">{t('app.accountSettings.passkeys.nameHelp') || 'Give your passkey a descriptive name (e.g., device name)'}</div>
                    <div className="cp-actions" style={{ marginTop: 12 }}>
                      <button type="button" className="cp-btn-secondary" onClick={() => { setShowPkNamePrompt(false); setPasskeyName(''); }}>{t('app.accountSettings.passkeys.cancel') || 'Cancel'}</button>
                      <button type="button" className="cp-btn-primary" onClick={registerPasskey}>{t('app.accountSettings.passkeys.continue') || 'Continue'}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="cp-actions" style={{ justifyContent: 'flex-start', marginBottom: 12 }}>
                          <button type="button" className="cp-btn-primary" onClick={registerPasskey}>{t('app.accountSettings.passkeys.registerNew') || 'Register new passkey'}</button>
                        </div>
                    <div className="cp-field">
                      <label>Registered passkeys</label>
                      {passkeys.length === 0 ? (
                        <div className="muted">{t('app.accountSettings.passkeys.noPasskeys') || 'No passkeys registered.'}</div>
                      ) : (
                        <div>
                          {passkeys.map((pk) => (
                            <div key={pk.credentialId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                              <div>
                                  <div>{pk.label}</div>
                                  <div className="muted">{t('app.accountSettings.passkeys.createdLabel') || 'Created'}: {pk.createdAt || 'Unknown'} | {t('app.accountSettings.passkeys.signCount') || 'Sign count'}: {pk.signCount}</div>
                                </div>
                                <button type="button" className="cp-btn-secondary" onClick={() => removePasskeyForAccount(pk.credentialId)}>{t('app.accountSettings.passkeys.remove') || 'Remove'}</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="muted">{pkMsg}</div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
